// Two decisions about MSAL that are pure, so they are tested here rather than
// discovered on a device.
//
// ⚠ WHY THIS FILE EXISTS. msal-browser v5 changed how a silent renewal gets its
// answer back. The hidden iframe is sent to the redirect URI — this app's own
// origin — and the PARENT no longer reads the iframe's URL. It waits on a
// BroadcastChannel for the redirect page to call `broadcastResponseToMainFrame`.
// This app never did, so on 2026-10-02, when conditional access expired a session
// (AADSTS70044), two things happened at once:
//   1. The whole SPA booted inside the hidden iframe, found the cached account,
//      asked for a token, and opened ANOTHER hidden iframe — about 20 nested
//      levels in 30 seconds, until Entra stopped answering (AADSTS50196, request
//      loop).
//   2. The parent waited out the bridge and failed with `timed_out`, which the
//      old check ("does the code include 'timeout'") did not recognise. So no
//      redirect to sign in, and the screen said the server had refused a request
//      it never received.
import { InteractionRequiredAuthError } from '@azure/msal-browser';

/** The shape of `window` this needs — narrow so a test can hand it a plain object. */
export interface FrameLike {
  self: unknown;
  top: unknown;
  opener: unknown;
  location: { hash: string; search: string };
}

/** True when the URL carries an authorization response: a `state`, plus a `code`
 *  or an `error`. MSAL asks for the fragment response mode, but a query response
 *  is checked too because that choice is the library's, not ours. */
export function hasAuthResponse(hash: string, search: string): boolean {
  for (const raw of [hash, search]) {
    const params = new URLSearchParams(raw.replace(/^[#?]/, ''));
    if (params.has('state') && (params.has('code') || params.has('error'))) return true;
  }
  return false;
}

/**
 * True when this page is MSAL's hidden iframe (or a popup) landing on the
 * redirect URI with an answer. That page's only job is to hand the answer to the
 * window that asked for it. It must never render the app: an app rendered there
 * has a signed-in account, asks for a token, and opens the next hidden iframe.
 *
 * A top-level page with a response in the URL is the ordinary sign-in redirect
 * coming home, and `handleRedirectPromise` in main.tsx deals with that.
 */
export function isMsalChildWindow(win: FrameLike): boolean {
  const inFrame = win.self !== win.top;
  const inPopup = Boolean(win.opener);
  return (inFrame || inPopup) && hasAuthResponse(win.location.hash, win.location.search);
}

/**
 * Whether a failed SILENT token request is one that only the person can fix by
 * signing in again — so the right move is a top-level redirect, not another
 * silent attempt.
 *
 * Includes timeouts: a renewal that does not answer is exactly the case a
 * first-party redirect fixes (blocked third-party cookies, a lost bridge
 * message). msal-browser v5 reports its bridge timeout as `timed_out` with
 * sub-error `redirect_bridge_timeout`, which contains no "timeout" — the old
 * substring test missed it, and that is half of what went wrong on 2026-10-02.
 */
export function needsInteraction(err: unknown): boolean {
  if (err instanceof InteractionRequiredAuthError) return true;
  const { errorCode, subError } = (err ?? {}) as { errorCode?: unknown; subError?: unknown };
  const code = String(errorCode ?? '');
  return code.includes('timeout') || code === 'timed_out' || subError === 'redirect_bridge_timeout';
}

/**
 * Where to put the person after the sign-in redirect comes home.
 *
 * The redirect URI is the origin, so without this a link straight to a page
 * (/op/receive, sent to a phone) lands on the line picker once Microsoft has
 * signed the person in, and the page they asked for is gone. The path goes out
 * as the login request's `state` and comes back on the redirect result.
 *
 * Only a path on THIS origin is honoured: it must start with one "/" and never
 * "//" or "/\" (both read as another host), and carry no scheme. Anything else
 * is ignored and the app opens where it would have.
 */
export function returnPathFrom(state: string | undefined | null): string | null {
  if (!state || state.length > 2048) return null;
  if (!state.startsWith('/') || state.startsWith('//') || state.startsWith('/\\')) return null;
  if (/[\u0000-\u001f\\]/.test(state)) return null;
  try {
    const url = new URL(state, 'https://origin.invalid');
    if (url.origin !== 'https://origin.invalid') return null;
    return url.pathname + url.search + url.hash;
  } catch {
    return null;
  }
}
